import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside, onKeyStroke } from '@vueuse/core'

import type { CSSProperties } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { TutorBillingExportType } from '@brighterly/lib-core-types/tutor/enums'

import { useListsStore } from '@shared/model'

import { TutorBillingModal } from './enums'
import { useTutorBillingStore } from './store'
import { resolveTutorBillingExportTypeOptions } from '../lib/tutor-billing-export'

interface TutorBillingExportMenuReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  items: ComputedRef<AppSelectOption<TutorBillingExportType>[]>
  setToggle: () => void
  setSelect: (type: TutorBillingExportType) => void
}

export const useTutorBillingExportMenu = (): TutorBillingExportMenuReturn => {
  const store = useTutorBillingStore()
  const listsStore = useListsStore()

  const isOpen = ref<boolean>(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const items = computed<AppSelectOption<TutorBillingExportType>[]>(() =>
    resolveTutorBillingExportTypeOptions(listsStore.getTutorBillingExportTypeOptions)
      .map(option => ({ value: option.value, text: `Export ${option.text}` })),
  )

  const setToggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const setClose = (): void => {
    isOpen.value = false
  }

  const setSelect = (type: TutorBillingExportType): void => {
    setClose()
    store.setExportType(type)
    store.openModal(TutorBillingModal.Export)
  }

  let stopListeners: (() => void)[] = []

  const setListeners = (open: boolean): void => {
    stopListeners.forEach(stop => stop())
    stopListeners = open ? [onClickOutside(containerRef, setClose), onKeyStroke('Escape', setClose)] : []
  }

  watch(isOpen, setListeners, { flush: 'sync' })

  onScopeDispose(() => setListeners(false))

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    items,
    setToggle,
    setSelect,
  }
}
